import { useState } from 'react';
import { useSessionStore } from '@/state/session-store';
import {
  isFeedbackEnabled,
  enableFeedback,
  getFeedbackSessions,
  exportFeedbackAsJson,
  generateFeedbackSummary,
} from '@/services/feedback-collector';

function formatDuration(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return min === 0 ? `${sec}s` : `${min}m ${sec}s`;
}

function formatTimestamp(timestamp: number, startedAt: number): string {
  const elapsed = timestamp - startedAt;
  const mins = Math.floor(elapsed / 60000);
  const secs = Math.floor((elapsed % 60000) / 1000);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function SessionSummary() {
  const { getSummary } = useSessionStore();
  const summary = getSummary();
  const [showTranscript, setShowTranscript] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  if (!summary) return null;

  const percent = Math.round((summary.coveredItems / summary.totalItems) * 100);

  const toggleExpanded = (id: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Get only final transcript segments for display
  const finalTranscripts = summary.transcript.filter(s => s.isFinal);

  return (
    <div className="space-y-4">
      {/* Overview Stats */}
      <div className="text-center pb-4 border-b border-gray-100">
        <div className="text-4xl font-bold text-gray-800 mb-1">{percent}%</div>
        <div className="text-sm text-gray-500">
          Coverage ({summary.coveredItems}/{summary.totalItems} items)
        </div>
        <div className="text-sm text-gray-400 mt-1">
          Duration: {formatDuration(summary.duration)}
        </div>
      </div>

      {/* Covered Items with Matched Text */}
      {summary.coveredItemsList.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-2 flex items-center gap-1">
            <span>Covered</span>
            <span className="text-green-500">({summary.coveredItemsList.length})</span>
          </h3>
          <div className="space-y-2">
            {summary.coveredItemsList.map(item => (
              <div
                key={item.id}
                className="bg-green-50 rounded-lg p-3 border border-green-100"
                style={{ marginLeft: (item.level || 0) * 12 }}
              >
                <div
                  className="flex items-start gap-2 cursor-pointer"
                  onClick={() => toggleExpanded(item.id)}
                >
                  <span className="text-green-500 mt-0.5">
                    {expandedItems.has(item.id) ? '▼' : '▶'}
                  </span>
                  <div className="flex-grow">
                    <div className="text-sm text-gray-700 line-through">{item.text}</div>
                    {item.coveredAt && (
                      <div className="text-xs text-green-600 mt-1">
                        Covered at {formatTimestamp(item.coveredAt, summary.startedAt)}
                        {item.confidence && (
                          <span className="ml-2 text-green-500">
                            ({Math.round(item.confidence * 100)}% match)
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {expandedItems.has(item.id) && item.matchedText && (
                  <div className="mt-2 ml-6 p-2 bg-white rounded border border-green-200">
                    <div className="text-xs text-gray-500 mb-1">What you said:</div>
                    <div className="text-sm text-gray-600 italic">"{item.matchedText}"</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Missed Items */}
      {summary.missedItems.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-2 flex items-center gap-1">
            <span>Missed</span>
            <span className="text-red-500">({summary.missedItems.length})</span>
          </h3>
          <div className="space-y-2">
            {summary.missedItems.map(item => (
              <div
                key={item.id}
                className="bg-red-50 rounded-lg p-3 border border-red-100"
                style={{ marginLeft: (item.level || 0) * 12 }}
              >
                <div className="flex items-start gap-2">
                  <span className="text-red-500">✗</span>
                  <span className="text-sm text-red-700">{item.text}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Perfect Score */}
      {summary.missedItems.length === 0 && (
        <div className="text-center py-4 bg-green-50 rounded-lg border border-green-200">
          <span className="text-2xl">Perfect!</span>
          <p className="text-green-700 font-medium mt-1">You covered everything.</p>
        </div>
      )}

      {/* Transcript Section */}
      <div className="border-t border-gray-100 pt-4">
        <button
          onClick={() => setShowTranscript(!showTranscript)}
          className="w-full flex items-center justify-between text-left"
        >
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Full Transcript ({finalTranscripts.length} segments)
          </h3>
          <span className="text-gray-400 text-xs">
            {showTranscript ? 'Hide' : 'Show'}
          </span>
        </button>

        {showTranscript && (
          <div className="mt-3 space-y-1 max-h-64 overflow-y-auto">
            {finalTranscripts.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No transcript recorded</p>
            ) : (
              finalTranscripts.map((segment, index) => (
                <div key={index} className="flex gap-2 text-sm">
                  <span className="text-gray-400 text-xs min-w-[40px]">
                    {formatTimestamp(segment.timestamp, summary.startedAt)}
                  </span>
                  <span className="text-gray-600">{segment.text}</span>
                </div>
              ))
            )}
          </div>
        )}

        {/* Full Transcript Text */}
        {showTranscript && summary.fullTranscriptText && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-500">Combined Transcript</span>
              <button
                onClick={() => navigator.clipboard.writeText(summary.fullTranscriptText)}
                className="text-xs text-blue-500 hover:text-blue-600"
              >
                Copy
              </button>
            </div>
            <p className="text-sm text-gray-600 whitespace-pre-wrap max-h-48 overflow-y-auto">
              {summary.fullTranscriptText}
            </p>
          </div>
        )}
      </div>

      {/* Analysis Summary */}
      {summary.fullTranscriptText && (
        <div className="border-t border-gray-100 pt-4">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Session Analysis
          </h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="bg-gray-50 p-2 rounded">
              <div className="text-gray-400 text-xs">Words Spoken</div>
              <div className="font-medium text-gray-700">
                {summary.fullTranscriptText.split(/\s+/).filter(w => w.length > 0).length}
              </div>
            </div>
            <div className="bg-gray-50 p-2 rounded">
              <div className="text-gray-400 text-xs">Avg. per Minute</div>
              <div className="font-medium text-gray-700">
                {Math.round(
                  (summary.fullTranscriptText.split(/\s+/).filter(w => w.length > 0).length /
                  (summary.duration / 60000)) || 0
                )} wpm
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Collection Section */}
      <FeedbackSection />
    </div>
  );
}

function FeedbackSection() {
  const [showDetails, setShowDetails] = useState(false);
  const feedbackEnabled = isFeedbackEnabled();
  const sessions = getFeedbackSessions();

  if (!feedbackEnabled) {
    return (
      <div className="border-t border-gray-100 pt-4">
        <button
          onClick={() => {
            enableFeedback();
            window.location.reload(); // Refresh to show enabled state
          }}
          className="text-xs text-blue-500 hover:text-blue-600"
        >
          Enable feedback collection (helps improve matching)
        </button>
      </div>
    );
  }

  return (
    <div className="border-t border-gray-100 pt-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Feedback Collection
        </h3>
        <span className="text-xs text-green-600">Enabled</span>
      </div>

      <div className="text-xs text-gray-500 mb-2">
        {sessions.length} session{sessions.length !== 1 ? 's' : ''} recorded
      </div>

      <div className="flex gap-2 flex-wrap">
        {sessions.length > 0 && (
          <>
            <button
              onClick={exportFeedbackAsJson}
              className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
            >
              Export All
            </button>
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
            >
              {showDetails ? 'Hide Stats' : 'Show Stats'}
            </button>
          </>
        )}
      </div>

      {showDetails && sessions.length > 0 && (
        <div className="mt-3 p-3 bg-gray-50 rounded-lg text-xs">
          <pre className="whitespace-pre-wrap text-gray-600 max-h-48 overflow-y-auto">
            {generateFeedbackSummary()}
          </pre>
        </div>
      )}
    </div>
  );
}
